import type { Driver, Tag, TagToCreate, User } from '../../../../../types';
import { DriverStatus } from '../../../../../types';
import {
    getMultiSelectTagsToAdd,
    getMultiSelectTagsToCreate,
    getMultiSelectTagsToRemove,
    getShowDeleteUserDialog,
    getUsersToDelete,
} from '../../../../Drivers.selectors';
import { DriverMultiSelect } from '../components/DriverMultiSelect';
import { clearAllMultiSelectTags } from '../../sidebar/tabs/groups/groupSlice';
import { getDriversRtk, getSelectedDriverIdRtk, getUsersRtk } from '../../../appSlice';
import { setUsersToDelete, toggleDeleteUserDialog } from '../../sidebar/tabs/user/userSlice';
import { useAppDispatch, useAppSelector } from '../../../../../configuration/setup/hooks';
import {
    showDeleteDriverSuccessNotification,
    showErrorNotification,
    showSuccessNotification,
} from '../../../notifications/notifications';
import {
    useDeleteDriverMutation,
    useLazyGetDriverQuery,
    useSaveDriverMutation,
    useTagDriverMutation,
    useUntagDriverMutation,
} from '../../../services/driversApi';
import { useCreateTagMutation } from '../../../services/tagsApi';
import { ErrorOrigin } from '../../../services/apiUtils';
import { getApiErrorCode, isApiErrorWithCode } from '../../../services/apiErrors';
import { deleteDriverSuccess, setSelectedDriver } from '../../sidebar/sidebarSlice';

export interface DriverMultiSelectPropertiesFromState {
    drivers: Driver[];
    tagsToCreate: TagToCreate[];
    tagsToAdd: Tag[];
    usersToDelete: User[];
    showDeleteUserDialog: boolean;
    tagsToRemove: Tag[];
    users: User[];
    currentDriverId: string | null;
}

export interface DriverMultiSelectPropertiesFromDispatch {
    clearMultiSelectTags: () => void;
    createTagsAndAddTagsToDrivers: (
        driverIds: string[],
        accountId: string,
        tagsToCreate: TagToCreate[],
        tagsToAdd: Tag[]
    ) => void;
    removeTagsFromDrivers: (drivers: Driver[], tagsToRemove: Tag[]) => void;
    archiveDrivers: (currentDriverId: string | null, drivers: Driver[], removeUsers: boolean) => void;
    activateDrivers: (currentDriverId: string | null, drivers: Driver[]) => void;
    toggleDeleteUserDialog: (show: boolean) => void;
    setUsersToDelete: (usersToDelete: User[]) => void;
    deleteDriver: (driver: Driver) => void;
}

interface DriverMultiSelectContainerOwnProps {
    selectedDriverIds: string[];
    toggleAllCheckboxes: (enable: boolean) => void;
}

const DriverMultiSelectContainerComponent = (props: DriverMultiSelectContainerOwnProps) => {
    const dispatch = useAppDispatch();

    const drivers = useAppSelector(getDriversRtk);
    const tagsToCreate = useAppSelector(getMultiSelectTagsToCreate);
    const tagsToAdd = useAppSelector(getMultiSelectTagsToAdd);
    const tagsToRemove = useAppSelector(getMultiSelectTagsToRemove);
    const users = useAppSelector(getUsersRtk);
    const showDeleteUserDialog = useAppSelector(getShowDeleteUserDialog);
    const usersToDelete = useAppSelector(getUsersToDelete);
    const currentDriverId = useAppSelector(getSelectedDriverIdRtk);

    const [createTagMutation] = useCreateTagMutation();
    const [tagDriverMutation] = useTagDriverMutation();
    const [untagDriverMutation] = useUntagDriverMutation();
    const [saveDriverMutation] = useSaveDriverMutation();
    const [deleteDriverMutation] = useDeleteDriverMutation();
    const [loadDriver] = useLazyGetDriverQuery();

    const handleDriverAdminError = (error: object) => {
        if (isApiErrorWithCode(error)) {
            showErrorNotification(getApiErrorCode(error, ErrorOrigin.DRIVER_ADMINISTRATION));
        }
    };

    // const handleTagError = (error: object) => {
    //     if (isApiErrorWithCode(error)) {
    //         showErrorNotification(getApiErrorCode(error, ErrorOrigin.TAG_SERVICE));
    //     }
    // };

    const refreshCurrentDriver = async (driverId: string | null) => {
        if (!driverId) {
            return;
        }

        const refreshedDriver = await loadDriver(driverId, true).unwrap();
        dispatch(setSelectedDriver(refreshedDriver));
    };

    const containerProps: DriverMultiSelectPropertiesFromState & DriverMultiSelectPropertiesFromDispatch = {
        drivers,
        tagsToCreate,
        tagsToAdd,
        usersToDelete,
        showDeleteUserDialog,
        tagsToRemove,
        users,
        currentDriverId,
        clearMultiSelectTags: () => {
            dispatch(clearAllMultiSelectTags());
        },
        createTagsAndAddTagsToDrivers: async (driverIds, accountId, tagsToCreateArg, tagsToAddArg) => {
            let errorOrigin: ErrorOrigin = ErrorOrigin.DRIVER_ADMINISTRATION;
            try {
                const createdTags: Tag[] = [];

                for (const tag of tagsToCreateArg) {
                    errorOrigin = ErrorOrigin.TAG_SERVICE;
                    createdTags.push(
                        await createTagMutation({
                            tagName: tag.name,
                            accountId,
                        }).unwrap()
                    );
                }
                errorOrigin = ErrorOrigin.DRIVER_ADMINISTRATION;

                const allTags = tagsToAddArg.concat(createdTags);
                for (const driverId of driverIds) {
                    for (const tag of allTags) {
                        await tagDriverMutation({ driverId, tagId: tag.id }).unwrap();
                    }
                    showSuccessNotification('intl-msg:save.success');
                }

                await refreshCurrentDriver(currentDriverId);
                dispatch(clearAllMultiSelectTags());
            } catch (error) {
                if (isApiErrorWithCode(error)) {
                    showErrorNotification(getApiErrorCode(error, errorOrigin));
                }
            }
        },
        removeTagsFromDrivers: async (driversArg, tagsToRemoveArg) => {
            try {
                for (const driver of driversArg) {
                    for (const tag of tagsToRemoveArg.filter(tagToRemove => driver.tags?.includes(tagToRemove.id))) {
                        await untagDriverMutation({ driverId: driver.driverId, tagId: tag.id }).unwrap();
                    }
                    showSuccessNotification('intl-msg:save.success');
                }

                await refreshCurrentDriver(currentDriverId);
                dispatch(clearAllMultiSelectTags());
            } catch (error) {
                handleDriverAdminError(error as object);
            }
        },
        archiveDrivers: async (selectedCurrentDriverId, driversArg, removeUsers) => {
            try {
                for (const driver of driversArg) {
                    await saveDriverMutation({
                        ...driver,
                        user: removeUsers ? undefined : driver.user,
                        subject: removeUsers ? undefined : driver.subject,
                        status: DriverStatus.ARCHIVED,
                    }).unwrap();
                    showSuccessNotification('intl-msg:save.success');
                }

                await refreshCurrentDriver(selectedCurrentDriverId);
            } catch (error) {
                handleDriverAdminError(error as object);
            }
        },
        activateDrivers: async (selectedCurrentDriverId, driversArg) => {
            try {
                for (const driver of driversArg) {
                    await saveDriverMutation({
                        ...driver,
                        status: DriverStatus.ACTIVE,
                    }).unwrap();
                    showSuccessNotification('intl-msg:save.success');
                }

                await refreshCurrentDriver(selectedCurrentDriverId);
            } catch (error) {
                handleDriverAdminError(error as object);
            }
        },
        setUsersToDelete: nextUsers => {
            dispatch(setUsersToDelete(nextUsers));
        },
        toggleDeleteUserDialog: show => {
            dispatch(toggleDeleteUserDialog(show));
        },
        deleteDriver: async driver => {
            try {
                await deleteDriverMutation(driver).unwrap();
                showDeleteDriverSuccessNotification(driver);
                dispatch(deleteDriverSuccess());
            } catch (error) {
                handleDriverAdminError(error as object);
            }
        },
    };

    return <DriverMultiSelect {...props} {...containerProps} />;
};

export const DriverMultiSelectContainer = DriverMultiSelectContainerComponent;
